import { Loader } from '@googlemaps/js-api-loader';
import { Controller } from '@hotwired/stimulus';

// Mapa somente-leitura das ocorrências de um alerta. Os pontos chegam da mais
// recente para a mais antiga; a numeração segue essa ordem e a linha tracejada
// liga a trilha em ordem cronológica.
export default class extends Controller {
  static targets = ['map', 'error']
  static values = { apiKey: String, points: Array }

  static LATEST_COLOR = '#DC2626'
  static EARLIER_COLOR = '#64748B'
  static MAX_ZOOM = 16
  // ~1 m: abaixo disso são leituras do mesmo lugar, não posições distintas.
  static COORDINATE_PRECISION = 5

  connect() {
    if (this.pointsValue.length === 0) return;

    new Loader({ apiKey: this.apiKeyValue, libraries: ['maps'] })
      .load()
      .then(() => this.draw())
      .catch(() => this.errorTarget.classList.remove('hidden'));
  }

  draw() {
    this.injectInfoWindowStyles();

    const map = new google.maps.Map(this.mapTarget, {
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    this.infoWindow = new google.maps.InfoWindow();

    const groups = this.groupByCoordinate(this.pointsValue);
    const bounds = new google.maps.LatLngBounds();

    groups.forEach((group, index) => {
      this.createMarker(map, group, index);
      bounds.extend({ lat: group.lat, lng: group.lng });
    });

    this.drawTrail(map, groups);
    map.fitBounds(bounds, 48);
    this.capZoom(map);
  }

  // Um veículo parado gera dezenas de ocorrências na mesma coordenada. Em vez
  // de empilhar pinos que se escondem (ou deslocá-los, o que mostraria uma
  // posição que não existiu), o lugar vira um marcador só com a contagem.
  groupByCoordinate(points) {
    const precision = this.constructor.COORDINATE_PRECISION;
    const groups = new Map();

    for (const point of points) {
      const key = `${point.lat.toFixed(precision)},${point.lng.toFixed(precision)}`;

      if (!groups.has(key)) groups.set(key, { lat: point.lat, lng: point.lng, points: [] });
      groups.get(key).points.push(point);
    }

    return [...groups.values()];
  }

  createMarker(map, group, index) {
    const isLatest = index === 0;
    const size = isLatest ? { width: 40, height: 50 } : { width: 32, height: 40 };

    const marker = new google.maps.Marker({
      map,
      position: { lat: group.lat, lng: group.lng },
      title: group.points[0].occurred_at,
      zIndex: isLatest ? 3 : 2,
      icon: {
        url: this.pinIcon(index + 1, group.points.length, isLatest),
        scaledSize: new google.maps.Size(size.width, size.height),
        // A ponta da gota fica em y=52 de um viewBox de 60.
        anchor: new google.maps.Point(size.width / 2, size.height * (52 / 60))
      }
    });

    marker.addListener('click', () => {
      this.infoWindow.setContent(this.bubble(group, index, isLatest));
      this.infoWindow.open(map, marker);
    });
  }

  // Ícone desenhado aqui em vez de AdvancedMarkerElement: aquele depende do
  // renderizador vetorial atrelado a um mapId, e um SVG em data URI aparece
  // em qualquer mapa — inclusive quando os tiles não carregam.
  pinIcon(number, count, isLatest) {
    const color = isLatest ? this.constructor.LATEST_COLOR : this.constructor.EARLIER_COLOR;
    const badge = count > 1 ? this.countBadge(count) : '';

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 60">
        <path d="M24 4C15.7 4 9 10.7 9 19c0 10.5 15 33 15 33s15-22.5 15-33c0-8.3-6.7-15-15-15z"
              fill="${color}" stroke="#ffffff" stroke-width="3"/>
        <text x="24" y="25" text-anchor="middle" fill="#ffffff"
              font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700">${number}</text>
        ${badge}
      </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.replace(/\s+/g, ' '))}`;
  }

  countBadge(count) {
    const label = `\u00D7${count}`;
    const width = 14 + label.length * 5;

    return `<g>
        <rect x="${46 - width}" y="0" rx="7" ry="7" width="${width}" height="16" fill="#111827" stroke="#ffffff" stroke-width="2"/>
        <text x="${46 - width / 2}" y="12" text-anchor="middle" fill="#ffffff"
              font-family="system-ui,-apple-system,sans-serif" font-size="10" font-weight="700">${label}</text>
      </g>`;
  }

  // Os pontos vêm do mais recente para o mais antigo; a trilha é desenhada ao
  // contrário para ser lida na ordem em que aconteceu.
  drawTrail(map, groups) {
    if (groups.length < 2) return;

    new google.maps.Polyline({
      map,
      path: [...groups].reverse().map((group) => ({ lat: group.lat, lng: group.lng })),
      strokeOpacity: 0,
      icons: [ {
        icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, strokeColor: this.constructor.EARLIER_COLOR, scale: 3 },
        offset: '0',
        repeat: '12px'
      } ]
    });
  }

  bubble(group, index, isLatest) {
    const [ newest, ...older ] = group.points;
    const badge = isLatest ? 'Ocorrência mais recente' : `Ocorrência ${index + 1}`;
    const summary = newest.summary ? `<div class="text-gray-900 font-medium">${newest.summary}</div>` : '';
    const others = older.length > 0
      ? `<div class="pt-1 text-gray-400">+ ${older.length} no mesmo local: ${older.map((point) => point.occurred_at).join(' · ')}</div>`
      : '';

    return `<div class="font-sans min-w-[200px] max-w-[280px] overflow-hidden rounded-xl">
        <div class="px-5 pt-4 pb-2">
          <span class="text-xs font-bold text-gray-900 uppercase tracking-wide">${badge}</span>
        </div>
        <div class="px-5 pb-5 text-xs text-gray-700 space-y-1">
          <div>${newest.occurred_at}</div>
          ${summary}
          ${others}
        </div>
      </div>`;
  }

  // fitBounds com um único ponto aproxima ao máximo e o mapa fica sem
  // referência nenhuma em volta.
  capZoom(map) {
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > this.constructor.MAX_ZOOM) map.setZoom(this.constructor.MAX_ZOOM);
    });
  }

  // monitoring/vehicles.scss pinta um fundo escuro global em .gm-style-iw-c
  // sem escopo nenhum; sem este override o balão deste mapa herda esse fundo
  // e o texto cinza-escuro fica ilegível.
  injectInfoWindowStyles() {
    if (document.getElementById('alert-event-map-iw-styles')) return;

    const style = document.createElement('style');
    style.id = 'alert-event-map-iw-styles';
    style.textContent = `
      .gm-style .gm-style-iw-c {
        padding: 0 !important;
        border-radius: 12px !important;
        box-shadow: none !important;
        background: #fff !important;
      }
      .gm-style .gm-style-iw-d { padding: 0 !important; }
      .gm-style .gm-style-iw-chr {
        position: absolute !important; top: 12px !important; right: 12px !important; height: auto !important;
      }
      .gm-style .gm-style-iw-chr button { width: 22px !important; height: 22px !important; opacity: 0.5 !important; }
      .gm-style .gm-style-iw-chr button:hover { opacity: 1 !important; }
      .gm-style .gm-style-iw-t::after { display: none !important; }
    `;
    document.head.appendChild(style);
  }
}
